<template>
	<div class="employers-page">
		<div class="employers-layout">
			<!-- Left Panel: Employer List -->
			<div class="employers-list-panel">
				<div class="list-header">
					<h2>Employers</h2>
				</div>
				<div class="search-box">
					<input
						v-model="searchQuery"
						type="text"
						placeholder="Search employers..."
						class="search-input"
					/>
				</div>
				<div class="employers-list">
					<div
						v-if="filteredEmployers.length > 0"
						class="employer-items"
					>
						<div
							v-for="employer in filteredEmployers"
							:key="employer.id"
							class="employer-item"
							:class="{ active: selectedEmployer && selectedEmployer.id === employer.id }"
							@click="selectEmployer(employer)"
						>
							{{ employer.name }}
						</div>
					</div>
					<div v-else class="empty-state">
						No employers found
					</div>
				</div>
			</div>

			<!-- Right Panel: Employer Details -->
			<div class="employer-details-panel">
				<div v-if="selectedEmployer" class="details-content">
					<!-- Employer ID -->
					<div class="detail-header">
						{{ selectedEmployer.id || 'New Employer' }}
					</div>

					<!-- Top Actions -->
					<div class="top-actions">
						<button class="action-btn primary" @click="saveEmployer">
							{{ selectedEmployer.id ? 'Save Changes' : 'Create Employer' }}
						</button>
						<button class="action-btn secondary" @click="createNewEmployer">New Employer</button>
						<button v-if="selectedEmployer.id" class="action-btn danger" @click="showDeleteConfirm = true">Delete Employer</button>
					</div>

					<!-- Basic Info Row -->
					<div class="form-row">
						<div class="form-group">
							<label>Name:</label>
							<input
								v-model="editingData.name"
								type="text"
								class="form-input"
							/>
						</div>
						<div class="form-group">
							<label>Logo:</label>
							<input
								v-model="editingData.logoUri"
								type="text"
								class="form-input"
							/>
						</div>
						<div class="form-group checkbox">
							<label>
								<input
									v-model="editingData.goldFeeStructure"
									type="checkbox"
									class="form-checkbox"
								/>
								Gold
							</label>
						</div>
					</div>

					<!-- Email Whitelist(s) -->
					<div class="form-group">
						<label>Email Whitelist(s):</label>
						<textarea
							v-model="editingData.emailDomain"
							class="form-textarea"
							rows="3"
						></textarea>
					</div>

					<!-- Licenses Section -->
					<div class="licenses-section">
						<div class="license-row">
							<div class="license-info">
								<span>Redeemed Licenses: {{ editingData.redeemedLicenses || 0 }}</span>
								<span>Available Licenses: {{ editingData.availableLicenses || 0 }}</span>
							</div>
							<button class="action-btn secondary" @click="openFreeLicensesDrawer">
								Show / Hide
							</button>
						</div>
					</div>

					<!-- Features Section -->
					<div class="features-section" v-if="selectedEmployer && selectedEmployer.features">
						<div class="features-header" @click="showFeatures = !showFeatures">
							<h3>Features ({{ selectedEmployer.features.length }})</h3>
							<span class="expand-icon" :class="{ rotated: showFeatures }">›</span>
						</div>
						<div v-if="showFeatures" class="features-list">
							<div v-for="feature in selectedEmployer.features" :key="feature.id" class="feature-item">
								<div class="feature-name">{{ feature.feature.name }}</div>
								<button
									class="feature-status"
									:class="{ enabled: feature.enabled, disabled: !feature.enabled }"
									@click="toggleFeature(feature)"
								>
									{{ feature.enabled ? 'Enabled' : 'Disabled' }}
								</button>
							</div>
						</div>
					</div>

					<!-- Action Buttons -->
					<!-- <div class="actions-row">
						<button class="action-btn secondary">Engagement Report</button>
					</div> -->

					<!-- Benefits Section -->
					<div class="form-group">
						<label>Employee Benefits Img Url:</label>
						<input
							v-model="editingData.benefitsHeaderImageUrl"
							type="text"
							class="form-input"
						/>
					</div>

					<div class="form-group">
						<label>Employee Benefits Description:</label>
						<textarea
							v-model="editingData.benefitsDescription"
							class="form-textarea"
							rows="4"
						></textarea>
					</div>

					<!-- Chat Context -->
					<div class="form-group">
						<label>Chat MG Context Info (the following info is about my employer.):</label>
						<textarea
							v-model="editingData.chatMGPrompt"
							class="form-textarea"
							rows="5"
						></textarea>
					</div>

					<!-- Keys Section -->
					<div class="form-group">
						<label>Keys (one per line):</label>
						<textarea
							v-model="editingData.keysText"
							placeholder="Paste keys here, one per line"
							class="form-textarea"
							rows="4"
						></textarea>
					</div>

					<div class="keys-actions">
						<button class="action-btn primary" @click="confirmUploadKeys">Upload Keys</button>
						<button class="action-btn danger" @click="confirmRemoveKeys">Remove Keys</button>
					</div>

					<!-- Knowledge Base Files Table -->
					<div class="knowledge-base-files-section">
						<AdminTable
							title="Knowledge Base Files"
							icon="📚"
							:columns="knowledgeBaseColumns"
							:data="knowledgeBaseFiles"
							:loading="false"
							:searchable="true"
							:deletable="true"
							@row-delete="deleteKnowledgeBaseFile"
							empty-message="No knowledge base files found."
						>
							<!-- <template #cell-fileUri="{ value }">
								<a v-if="value" :href="value" target="_blank" rel="noopener noreferrer">View File</a>
							</template> -->
						</AdminTable>
					</div>

					<!-- Knowledge Base Upload -->
					<div class="form-group">
						<label>Knowledge Base File Upload</label>
						<div class="file-upload-container">
							<input
								ref="fileInput"
								type="file"
								class="form-input"
								@change="handleFileSelect"
							/>
							<button
								v-if="selectedFile"
								class="action-btn primary"
								:disabled="!selectedFileDescription.trim()"
								@click="uploadFile"
							>
								Upload File
							</button>
						</div>
						<div v-if="selectedFile" class="file-info-container">
							<div class="file-name">
								Selected: {{ selectedFile.name }}
							</div>
							<label class="file-description-label">"The knowledge base contains a document with information about: {{ selectedFileDescription }}"</label>
							<textarea
								v-model="selectedFileDescription"
								class="form-textarea file-description-textarea"
								placeholder="Enter a description for the file..."
								rows="3"
							></textarea>
						</div>
					</div>
				</div>
				<div v-else class="empty-state">
					Select an employer to view details
				</div>
			</div>
		</div>

		<!-- Free Licenses Drawer -->
		<div v-if="showLicensesDrawer" class="drawer-overlay" @click="showLicensesDrawer = false">
			<div class="drawer" @click.stop>
				<div class="drawer-header">
					<h3>Free Licenses</h3>
					<button class="close-btn" @click="showLicensesDrawer = false">✕</button>
				</div>
				<div class="drawer-content">
					<div v-if="licensesLoading" class="loading">Loading licenses...</div>
					<div v-else-if="freeLicenses.length > 0" class="licenses-grid">
						<div v-for="(license, index) in freeLicenses" :key="index" class="license-item">
							{{ license }}
						</div>
					</div>
					<div v-else class="empty">No free licenses available</div>
				</div>
			</div>
		</div>

		<!-- Upload Keys Confirmation Dialog -->
		<div v-if="showKeysConfirm" class="modal-overlay" @click="showKeysConfirm = false">
			<div class="modal-dialog" @click.stop>
				<div class="modal-header">
					<h3>Confirm Upload</h3>
					<button class="close-btn" @click="showKeysConfirm = false">✕</button>
				</div>
				<div class="modal-body">
					<p>Are you sure you want to upload {{ keysToUpload.length }} key(s)?</p>
				</div>
				<div class="modal-footer">
					<button class="action-btn primary" @click="uploadKeys">Upload</button>
					<button class="action-btn secondary" @click="showKeysConfirm = false">Cancel</button>
				</div>
			</div>
		</div>

		<!-- Remove Keys Confirmation Dialog -->
		<div v-if="showRemoveKeysConfirm" class="modal-overlay" @click="showRemoveKeysConfirm = false">
			<div class="modal-dialog" @click.stop>
				<div class="modal-header">
					<h3>Confirm Removal</h3>
					<button class="close-btn" @click="showRemoveKeysConfirm = false">✕</button>
				</div>
				<div class="modal-body">
					<p>Are you sure you want to remove all keys? This action cannot be undone.</p>
				</div>
				<div class="modal-footer">
					<button class="action-btn danger" @click="removeKeys">Remove Keys</button>
					<button class="action-btn secondary" @click="showRemoveKeysConfirm = false">Cancel</button>
				</div>
			</div>
		</div>

		<!-- Delete Employer Confirmation Dialog -->
		<div v-if="showDeleteConfirm" class="modal-overlay" @click="showDeleteConfirm = false">
			<div class="modal-dialog delete-modal" @click.stop>
				<div class="modal-header">
					<h3>Delete Employer</h3>
					<button class="close-btn" @click="showDeleteConfirm = false">✕</button>
				</div>
				<div class="modal-body">
					<p class="warning-text">
						⚠️ This action cannot be undone. All data associated with this employer will be permanently deleted.
					</p>
					<div class="delete-confirm-group">
						<label class="checkbox-label">
							<input
								v-model="deleteAcknowledge"
								type="checkbox"
								class="form-checkbox"
							/>
							I understand the consequences of this action
						</label>
					</div>
					<div class="delete-confirm-group">
						<label>Type to confirm deletion:</label>
						<div class="confirm-hint">
							Type: <strong>DELETE {{ selectedEmployer.id }}</strong>
						</div>
						<input
							v-model="deleteConfirmText"
							type="text"
							class="form-input"
							:placeholder="`DELETE ${selectedEmployer.id}`"
						/>
					</div>
				</div>
				<div class="modal-footer">
					<button
						class="action-btn danger"
						:disabled="!deleteAcknowledge || !deleteConfirmValid"
						@click="proceedToFinalDeleteConfirm"
					>
						Proceed to Delete
					</button>
					<button class="action-btn secondary" @click="showDeleteConfirm = false">Cancel</button>
				</div>
			</div>
		</div>

		<!-- Final Delete Confirmation Dialog -->
		<div v-if="showFinalDeleteConfirm" class="modal-overlay" @click="showFinalDeleteConfirm = false">
			<div class="modal-dialog delete-modal" @click.stop>
				<div class="modal-header">
					<h3>Confirm Deletion</h3>
					<button class="close-btn" @click="showFinalDeleteConfirm = false">✕</button>
				</div>
				<div class="modal-body">
					<p class="final-warning-text">
						⚠️ You are about to permanently delete:
					</p>
					<div class="employer-name-display">
						{{ selectedEmployer.name }}
					</div>
					<p style="margin-top: 20px; color: var(--admin-text-secondary); font-size: 13px;">
						This action cannot be undone. All associated data will be deleted permanently.
					</p>
				</div>
				<div class="modal-footer">
					<button
						class="action-btn danger"
						@click="confirmDeleteEmployer"
					>
						Yes, Delete Permanently
					</button>
					<button class="action-btn secondary" @click="showFinalDeleteConfirm = false">Cancel</button>
				</div>
			</div>
		</div>

		<!-- Success Toast Notification -->
		<v-snackbar
			v-model="showSuccessToast"
			:timeout="3000"
			location="bottom"
			color="success"
		>
			{{ successMessage }}
			<template #actions>
				<v-btn
					icon="mdi-close"
					size="small"
					@click="showSuccessToast = false"
				></v-btn>
			</template>
		</v-snackbar>

		<!-- Error Toast Notification -->
		<v-snackbar
			v-model="showErrorToast"
			:timeout="4000"
			location="bottom"
			color="error"
		>
			{{ errorMessage }}
			<template #actions>
				<v-btn
					icon="mdi-close"
					size="small"
					@click="showErrorToast = false"
				></v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';
import AdminTable from '@/admin/components/AdminTable.vue';

export default {
	name: 'EmployersPage',
	mixins: [AdminServices],
	components: {
		AdminTable
	},
	data() {
		return {
			employers: [],
			selectedEmployer: null,
			editingData: {},
			searchQuery: '',
			showLicenses: false,
			showFeatures: false,
			showLicensesDrawer: false,
			freeLicenses: [],
			licensesLoading: false,
			employersLoading: false,
			showKeysConfirm: false,
			showRemoveKeysConfirm: false,
			keysToUpload: [],
			showSuccessToast: false,
			successMessage: '',
			showErrorToast: false,
			errorMessage: '',
			selectedFile: null,
			selectedFileDescription: '',
			knowledgeBaseFiles: [],
			showDeleteConfirm: false,
			deleteConfirmText: '',
			deleteAcknowledge: false,
			showFinalDeleteConfirm: false
		};
	},
	computed: {
		filteredEmployers() {
			if (!this.searchQuery.trim()) {
				return this.employers;
			}
			const query = this.searchQuery.toLowerCase();
			return this.employers.filter(emp => emp.name.toLowerCase().includes(query));
		},
		deleteConfirmValid() {
			const expectedText = `DELETE ${this.selectedEmployer.id}`;
			return this.deleteConfirmText === expectedText;
		},
		knowledgeBaseColumns() {
			return [
				{ field: 'fileName', label: 'File Name', width: '2fr', sortable: true },
				{ field: 'description', label: 'Description', width: '3fr' },
				{ field: 'createDateTime', label: 'Date Uploaded', width: '1.5fr', sortable: true, type: 'date' },
				{ field: 'fileUri', label: 'File Link', width: '1fr' } // New column for fileUri
			];
		}
	},
	methods: {
		showToast(message) {
			this.successMessage = message;
			this.showSuccessToast = true;
		},
		showError(message) {
			this.errorMessage = message;
			this.showErrorToast = true;
		},
		selectEmployer(employer) {
			this.selectedEmployer = employer;
			this.knowledgeBaseFiles = [];
			// Fetch full employer details
			this._getEmployerDetails(employer.id, (fullDetails) => {
				this.selectedEmployer = fullDetails;
				// Create a copy of the employer data for editing
				this.editingData = {
					name: fullDetails.name,
					logoUri: fullDetails.logoUri,
					emailDomain: fullDetails.emailDomain,
					goldFeeStructure: fullDetails.goldFeeStructure,
					benefitsHeaderImageUrl: fullDetails.benefitsHeaderImageUrl,
					benefitsDescription: fullDetails.benefitsDescription,
					chatMGPrompt: fullDetails.chatMGPrompt,
					availableLicenses: fullDetails.availableLicenses,
					redeemedLicenses: fullDetails.redeemedLicenses,
					keysText: ''
				};
				this._getEmployerKnowledgeBaseFiles(this.selectedEmployer.id, (files) => {
					this.knowledgeBaseFiles = files;
				}, () => {
					this.showError('Failed to load knowledge base files');
				});
			});
		},
		saveEmployer() {
			const employerData = {
				name: this.editingData.name,
				logoUri: this.editingData.logoUri,
				emailDomain: this.editingData.emailDomain,
				goldFeeStructure: this.editingData.goldFeeStructure,
				benefitsHeaderImageUrl: this.editingData.benefitsHeaderImageUrl,
				benefitsDescription: this.editingData.benefitsDescription,
				chatMGPrompt: this.editingData.chatMGPrompt
			};

			// Check if this is a new employer (no ID) or existing
			if (!this.selectedEmployer.id) {
				// Create new employer
				this._createEmployer(employerData, () => {
					this.showToast('Employer created successfully');
				}, (error) => {
					this.showError(error);
				});
			} else {
				// Update existing employer
				const updateData = {
					id: this.selectedEmployer.id,
					...employerData,
					createDateTime: this.selectedEmployer.createDateTime,
					features: this.selectedEmployer.features,
					availableLicenses: this.selectedEmployer.availableLicenses,
					redeemedLicenses: this.selectedEmployer.redeemedLicenses,
					hashGatewayKeys: this.selectedEmployer.hashGatewayKeys
				};
				this._updateEmployer(this.selectedEmployer.id, updateData, () => {
					// Refresh the employers list
					this._getEmployers();
					this.showToast('Employer updated successfully');
				}, (error) => {
					this.showError(error);
				});
			}
		},
		confirmUploadKeys() {
			const keys = this.editingData.keysText.split('\n').filter(key => key.trim() !== '');
			if (keys.length === 0) {
				this.showToast('Please enter some keys to upload');
				return;
			}
			this.keysToUpload = keys;
			this.showKeysConfirm = true;
		},
		uploadKeys() {
			this._uploadKeys(this.selectedEmployer.id, this.keysToUpload, () => {
				this.showKeysConfirm = false;
				this.editingData.keysText = '';
				this.keysToUpload = [];
				this.showToast('Keys uploaded successfully');
			}, (error) => {
				this.showError(error);
			});
		},
		confirmRemoveKeys() {
			this.showRemoveKeysConfirm = true;
		},
		removeKeys() {
			this._removeKeys(this.selectedEmployer.id, [], () => {
				this.showRemoveKeysConfirm = false;
				this.showToast('Keys removed successfully');
			}, (error) => {
				this.showError(error);
			});
		},
		toggleShowLicenses() {
			this.showLicenses = !this.showLicenses;
		},
		openFreeLicensesDrawer() {
			this.licensesLoading = true;
			this.freeLicenses = [];
			this.showLicensesDrawer = true;
			this._getFreeLicenses(this.selectedEmployer.id, (licenses) => {
				this.freeLicenses = licenses;
				this.licensesLoading = false;
			});
		},
		toggleFeature(feature) {
			feature.enabled = !feature.enabled;
			this.saveEmployer();
		},
		handleFileSelect(event) {
			const file = event.target.files[0];
			if (file) {
				this.selectedFile = file;
				this.selectedFileDescription = '';
			}
		},
		deleteKnowledgeBaseFile(file) {
			this._deleteEmployerKnowledgeBaseFile(this.selectedEmployer.id, file.id, () => {
				this.showToast('File deleted from Knowledge Base successfully');
				this._getEmployerKnowledgeBaseFiles(this.selectedEmployer.id, (files) => {
					this.knowledgeBaseFiles = files;
				}, () => {
					this.showError('Failed to refresh knowledge base files');
				});
			}, () => {
				this.showError('Failed to delete file');
			});
		},
		uploadFile() {
			if (!this.selectedFile) {
				this.showError('Please select a file');
				return;
			}

			const reader = new FileReader();
			reader.onload = (e) => {
				// The result is a data URL: "data:mime/type;base64,..."
				// We need to extract just the base64 part.
				const dataUrl = e.target.result;
				const base64Content = dataUrl.split(',')[1];

				const payload = {
					content: base64Content,
					fileName: this.selectedFile.name,
					description: this.selectedFileDescription
				};

				this._uploadEmployerKnowledgeBase(
					this.selectedEmployer.id,
					payload,
					() => {
						this.showToast('File uploaded to Knowledge Base successfully');
						this.selectedFile = null;
						this.selectedFileDescription = '';
						this.$refs.fileInput.value = '';
						this._getEmployerKnowledgeBaseFiles(this.selectedEmployer.id, (files) => {
							this.knowledgeBaseFiles = files;
						}, () => {
							this.showError('Failed to refresh knowledge base files');
						});
					},
					() => {
						this.showError('Failed to upload file');
					}
				);
			};
			reader.onerror = () => {
				this.showError('Error reading file');
			};
			reader.readAsDataURL(this.selectedFile);
		},
		createNewEmployer() {
			const newEmployer = {
				id: '',
				name: '',
				logoUri: '',
				emailDomain: '',
				goldFeeStructure: false,
				benefitsHeaderImageUrl: '',
				benefitsDescription: '',
				chatMGPrompt: '',
				availableLicenses: 0,
				redeemedLicenses: 0,
				createDateTime: new Date().toISOString(),
				features: [],
				hashGatewayKeys: []
			};

			this.selectedEmployer = newEmployer;
			this.editingData = {
				name: '',
				logoUri: '',
				emailDomain: '',
				goldFeeStructure: false,
				benefitsHeaderImageUrl: '',
				benefitsDescription: '',
				chatMGPrompt: '',
				availableLicenses: 0,
				redeemedLicenses: 0,
				keysText: ''
			};
		},
		proceedToFinalDeleteConfirm() {
			this.showDeleteConfirm = false;
			this.showFinalDeleteConfirm = true;
		},
		confirmDeleteEmployer() {
			this._deleteEmployer(this.selectedEmployer.id, () => {
				this.showFinalDeleteConfirm = false;
				this.deleteConfirmText = '';
				this.deleteAcknowledge = false;
				this.selectedEmployer = null;
				this.showToast('Employer deleted successfully');
			}, (error) => {
				this.showError(error);
			});
		}
	},
	mounted() {
		console.log('Employers Page loaded');
		this._getEmployers();
	}
};
</script>

<style scoped>
.employers-page {
	min-height: 100%;
	padding: 20px;
}

.employers-layout {
	display: grid;
	grid-template-columns: 300px 1fr;
	gap: 20px;
	height: 100%;
}

/* Left Panel: Employer List */
.employers-list-panel {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	display: flex;
	flex-direction: column;
	box-shadow: var(--box-shadow);
}

.list-header {
	margin-bottom: 15px;
}

.list-header h2 {
	margin: 0;
	font-size: 16px;
	color: var(--admin-text-primary);
}

.employers-list {
	flex: 1;
	overflow-y: auto;
}

.employer-items {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.employer-item {
	padding: 12px;
	background-color: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	cursor: pointer;
	border-radius: var(--border-radius-s);
	font-size: 13px;
	transition: all 0.2s ease;
	border-left: 3px solid transparent;
}

.employer-item:hover {
	background-color: var(--admin-hover-bg);
}

.employer-item.active {
	background-color: var(--admin-active-bg);
	border-left-color: var(--background-color-primary);
	font-weight: 500;
}

/* Right Panel: Employer Details */
.employer-details-panel {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 30px;
	overflow-y: auto;
	box-shadow: var(--box-shadow);
}

.details-content {
	max-width: 900px;
}

.detail-header {
	font-size: 13px;
	color: var(--admin-text-secondary);
	margin-bottom: 20px;
	padding: 10px;
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-s);
	font-family: monospace;
}

/* Form Styles */
.form-row {
	display: grid;
	grid-template-columns: 2fr 1.5fr auto;
	gap: 15px;
	margin-bottom: 20px;
}

.form-group {
	margin-bottom: 20px;
}

.form-group.checkbox {
	flex-direction: row;
	align-items: center;
	margin-bottom: 0;
}

.form-group.checkbox label {
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 0;
	font-weight: normal;
}

input[type="file"].form-input::file-selector-button {
	padding: 6px 12px;
	margin-right: 10px;
	background-color: var(--background-color-primary);
	color: white;
	border: none;
	border-radius: var(--border-radius-s);
	cursor: pointer;
	font-size: 12px;
	font-weight: 500;
	transition: all 0.2s ease;
}

input[type="file"].form-input::file-selector-button:hover {
	background-color: #332847;
	box-shadow: 0px 2px 8px 0px rgba(51, 40, 71, 0.2);
}

/* File Upload */
.file-upload-container {
	display: flex;
	gap: 10px;
	align-items: center;
	margin-bottom: 10px;
}

.file-upload-container .form-input {
	flex: 1;
}

.file-info-container {
	margin-top: 10px;
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.file-name {
	font-size: 12px;
	color: var(--admin-text-secondary);
	padding: 8px;
	background-color: var(--admin-hover-bg);
	border-radius: var(--border-radius-s);
}

.file-description-textarea {
	width: 100%;
	min-height: 80px; /* Adjust as needed */
	box-sizing: border-box; /* Include padding and border in the element's total width and height */
}

/* Licenses Section */
.licenses-section {
	background-color: var(--admin-bg-secondary);
	padding: 15px;
	border-radius: var(--border-radius-s);
	margin-bottom: 20px;
}

.license-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 15px;
}

.license-info {
	display: flex;
	gap: 20px;
	font-size: 13px;
	color: var(--admin-text-primary);
}

/* Features Section */
.features-section {
	background-color: var(--admin-bg-secondary);
	padding: 15px;
	border-radius: var(--border-radius-s);
	margin-bottom: 20px;
	border: 1px solid var(--admin-border);
}

.features-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	cursor: pointer;
	user-select: none;
}

.features-header h3 {
	margin: 0;
	font-size: 14px;
	color: var(--admin-text-primary);
	font-weight: 500;
}

.expand-icon {
	font-size: 18px;
	transition: transform 0.3s ease;
	color: var(--admin-text-secondary);
}

.expand-icon.rotated {
	transform: rotate(90deg);
}

.features-list {
	margin-top: 12px;
	display: flex;
	flex-direction: column;
	gap: 8px;
	max-height: 400px;
	overflow-y: auto;
}

.feature-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 10px;
	background-color: var(--admin-hover-bg);
	border-radius: var(--border-radius-s);
	font-size: 13px;
}

.feature-name {
	color: var(--admin-text-primary);
	flex: 1;
}

.feature-status {
	padding: 4px 8px;
	border-radius: 4px;
	font-size: 11px;
	font-weight: 500;
	min-width: 70px;
	text-align: center;
	border: none;
	cursor: pointer;
	transition: all 0.2s ease;
}

.feature-status.enabled {
	background-color: #4caf50;
	color: white;
}

.feature-status.enabled:hover {
	background-color: #388e3c;
	transform: scale(1.05);
}

.feature-status.disabled {
	background-color: #f44336;
	color: white;
}

.feature-status.disabled:hover {
	background-color: #d32f2f;
	transform: scale(1.05);
}

/* Top Actions */
.top-actions {
	display: flex;
	gap: 10px;
	margin-bottom: 30px;
	padding-bottom: 20px;
	border-bottom: 1px solid var(--admin-border);
}

.actions-row {
	display: flex;
	gap: 10px;
	margin-bottom: 20px;
}

/* Keys Actions */
.keys-actions {
	display: flex;
	gap: 10px;
	margin-bottom: 20px;
}

/* Save Actions */
.save-actions {
	display: flex;
	gap: 10px;
	margin-top: 30px;
	padding-top: 20px;
	border-top: 1px solid var(--admin-border);
}

/* Drawer Overlay */
.drawer-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: flex-end;
	z-index: 1000;
	animation: fadeIn 0.2s ease;
}

.drawer {
	background-color: var(--admin-bg-secondary);
	width: 400px;
	height: 80vh;
	display: flex;
	flex-direction: column;
	box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
	animation: slideIn 0.3s ease;
	margin-top: 100px;
}

@keyframes slideIn {
	from { transform: translateX(100%); }
	to { transform: translateX(0); }
}

.drawer-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px;
	padding-top: calc(20px + max(4px, env(safe-area-inset-top)));
	border-bottom: 1px solid var(--admin-border);
}

.drawer-header h3 {
	margin: 0;
	font-size: 18px;
	color: var(--admin-text-primary);
}

.drawer-content {
	flex: 1;
	padding: 20px;
	overflow-y: auto;
}

.licenses-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
}

.license-item {
	background-color: var(--admin-hover-bg);
	border: 1px solid var(--admin-border);
	padding: 12px;
	border-radius: var(--border-radius-s);
	font-size: 13px;
	color: var(--admin-text-primary);
	text-align: center;
	font-family: monospace;
	word-break: break-all;
}

.drawer-content .loading,
.drawer-content .empty {
	text-align: center;
	color: var(--admin-text-secondary);
	padding: 20px;
}

/* Delete Modal Styles */
.delete-modal {
	max-width: 500px;
}

.warning-text {
	color: #f44336;
	font-weight: 500;
	margin-bottom: 20px;
	padding: 12px;
	background-color: rgba(244, 67, 54, 0.1);
	border-left: 3px solid #f44336;
	border-radius: var(--border-radius-s);
}

.delete-confirm-group {
	margin-bottom: 20px;
}

.delete-confirm-group label {
	display: block;
	margin-bottom: 8px;
	font-size: 13px;
	color: var(--admin-text-primary);
	font-weight: 500;
}

.checkbox-label {
	display: flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;
	font-weight: normal;
	margin-bottom: 0;
}

.modal-footer .action-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.confirm-hint {
	font-size: 12px;
	color: var(--admin-text-secondary);
	margin-bottom: 8px;
	padding: 8px;
	background-color: var(--admin-hover-bg);
	border-radius: var(--border-radius-s);
	font-family: monospace;
}

.employer-name-display {
	font-size: 18px;
	font-weight: 500;
	color: var(--admin-text-primary);
	padding: 15px;
	background-color: var(--admin-hover-bg);
	border-radius: var(--border-radius-s);
	border-left: 3px solid #f44336;
	word-break: break-all;
}

.final-warning-text {
	color: #f44336;
	font-weight: 500;
	margin-bottom: 12px;
}

/* Responsive */
@media (max-width: 768px) {
	.drawer {
		width: 100%;
	}

	.licenses-grid {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 1024px) {
	.employers-layout {
		grid-template-columns: 250px 1fr;
	}
}

/* Snackbar positioning - much lower on screen */
:deep(.v-snackbar) {
	bottom: 100px !important;
	top: auto !important;
	margin-top: 0 !important;
}

/* Mobile Responsive */
@media (max-width: 480px) {
	.employers-layout {
		grid-template-columns: 1fr;
		gap: 15px;
	}

	.employers-list-panel {
		max-height: 400px;
	}

	.form-row {
		grid-template-columns: 1fr;
	}

	.employers-page {
		padding: 10px;
	}

	/* File Upload adjustments for mobile */
	.file-upload-container {
		flex-direction: column;
		align-items: stretch;
		gap: 10px;
	}

	.file-upload-container .form-input {
		width: 100%; /* Make file input full width */
	}

	.file-upload-container .action-btn {
		width: 100%; /* Make upload button full width */
	}

	.file-info-container {
		align-items: stretch;
	}

	.file-name,
	.file-description-textarea {
		width: 100%; /* Ensure these also take full width */
	}
}
</style>
